// ADR-100 Decision 7 — the Definition's translation in Learn.
//
// The control only. It lives in the pinned zone's 44 px left column, which
// already exists to balance the reference trigger on the right, is always
// mounted, and shows availability by opacity/visibility alone — so the column
// is identical with or without a translation.
//
// What it opens is not a layer: Learn replaces each English text with its
// translation in its own place (the prompt, the fill-in-blank sentence and its
// caption) and re-measures it. The card and the answer input never move — see
// LearnPage.
//
// The control is also the only sign that the card is showing translations
// (owner's instruction 2026-09-21 — the inline language marker on the swapped
// text is gone): filled with the accent and a white label while on, outlined
// while off, and a pressed toggle (`aria-pressed`) to assistive technology.
//
// Opening is not a hint and never sets `hint_used` (ADR-100 Decision 4).
import { useLocale } from '../../i18n/context'

export function DefinitionTranslationToggle({
  available,
  open,
  language,
  controlsId,
  onToggle,
}: {
  available: boolean
  open: boolean
  /** The reader's source_language; its code is the control's visible face. */
  language: string
  controlsId: string
  onToggle: () => void
}) {
  const { tr } = useLocale()
  const active = available && open
  return (
    <button
      type="button"
      data-testid="definition-translation-toggle"
      aria-pressed={active}
      aria-controls={available ? controlsId : undefined}
      aria-label={tr.learn.translateCard}
      title={tr.learn.translateCard}
      aria-hidden={!available}
      tabIndex={available ? 0 : -1}
      // Never take focus from the answer input on a tap or click: a blurred
      // input retracts the soft keyboard (ADR-011/013). Keyboard activation
      // (Tab → Enter) is handled by the caller, which refocuses the input.
      onPointerDown={(e) => e.preventDefault()}
      onMouseDown={(e) => e.preventDefault()}
      onClick={available ? onToggle : undefined}
      style={{
        width: 32,
        height: 32,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        borderRadius: 50,
        border: `1.5px solid ${active ? 'var(--accent-fill)' : 'var(--border)'}`,
        // The design system's accent-button pair (DESIGN_SYSTEM.md "Contrast
        // pairs"): --accent-fill under --text-on-accent holds 4.5:1 in both
        // themes, which this 10 px label needs. Plain --accent does not in dark.
        background: active ? 'var(--accent-fill)' : 'none',
        color: active ? 'var(--text-on-accent)' : 'var(--text-secondary)',
        fontFamily: 'var(--font-ui)',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.06em',
        lineHeight: 1,
        textTransform: 'uppercase',
        cursor: available ? 'pointer' : 'default',
        transition: 'color 0.15s, border-color 0.15s, background-color 0.15s, opacity 0.2s',
        opacity: available ? 1 : 0,
        visibility: available ? 'visible' : 'hidden',
        pointerEvents: available ? 'auto' : 'none',
      }}
    >
      {language}
    </button>
  )
}
